import { css } from '@emotion/react';
import {
  ActionIcon,
  AspectRatio,
  Box,
  Button,
  Flex,
  Icon,
  OptimizeImage,
  Stack,
  Text,
  Title,
  desktopFirstMediaQuery,
  mobileFirstMediaQuery,
} from '@inflearn/ds-react';
import { faAngleRight } from '@inflearn/pro-regular-svg-icons';
import { Trans, useTranslation } from 'next-i18next';

import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { JOIN_INSTRUCTOR_CONTENT_TYPES } from './constants/gtmEventParams';

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  const INTERVIEWS = [
    {
      title: t('open.knowledge.testimonial.interview1.title'),
      description: t('open.knowledge.testimonial.interview1.description'),
      name: t('open.knowledge.testimonial.interview1.name'),
      imageUrl:
        'https://cdn.inflearn.com/public/files/posts/367f6f53-c1e8-4e22-80d1-d41c90368f54/ljh.png',
      articleLink: 'https://www.inflearn.com/pages/instructor-interview-01',
    },
    {
      title: t('open.knowledge.testimonial.interview2.title'),
      description: t('open.knowledge.testimonial.interview2.description'),
      name: t('open.knowledge.testimonial.interview2.name'),
      imageUrl:
        'https://cdn.inflearn.com/public/files/posts/7fac03bd-95aa-4286-a265-c1e408ebf242/rks.png',
      articleLink: 'https://www.inflearn.com/pages/instructor-interview-03',
    },
    {
      title: t('open.knowledge.testimonial.interview3.title'),
      description: t('open.knowledge.testimonial.interview3.description'),
      name: t('open.knowledge.testimonial.interview3.name'),
      imageUrl:
        'https://cdn.inflearn.com/public/files/posts/a5ce0885-9844-40a4-9969-8fac1dff771b/eunjae.png',
      articleLink: 'https://www.inflearn.com/pages/instructor-interview-04',
    },
  ];

  return (
    <Box component="section" css={styleSection}>
      <Flex direction="column" align="center" css={styleContainer}>
        <Stack spacing={40} css={styleContents}>
          <Stack spacing={16} align="center" css={styleTitleStack}>
            <Title order={2} align="center" color="dark.9" css={styleSectionTitle}>
              {t('open.knowledge.testimonial.title')}
            </Title>
            <Text color="gray.9" css={styleSubTitle}>
              {t('open.knowledge.testimonial.subtitle')}
            </Text>
          </Stack>

          {/* Interview cards */}
          <Flex gap={20} css={styleCardRow}>
            {INTERVIEWS.map((interview) => (
              <Stack key={interview.name} css={styleCard}>
                <AspectRatio ratio={16 / 9} css={styleCardImage}>
                  <OptimizeImage src={interview.imageUrl} width="100%" w={365 * 2} />
                </AspectRatio>
                <Stack spacing={8} css={styleCardContent}>
                  <Text color="gray.9" css={styleCardTitle}>
                    {interview.title}
                  </Text>
                  <Text color="gray.9" css={styleCardDesc} mih={48}>
                    {interview.description}
                  </Text>
                  <Flex align="center" justify="space-between" css={styleCardFooter}>
                    <Text color="gray.9" css={styleCardName}>
                      {interview.name}
                    </Text>

                    <ArticleButton
                      link={interview.articleLink}
                      onTrack={() =>
                        trackMixpanelEvent(eventNames.CLICK_JOIN_INSTRUCTOR_CONTENT, {
                          content_type: JOIN_INSTRUCTOR_CONTENT_TYPES.INTERVIEW_LINK,
                          content_name: `interview_${interview.name}`,
                        })
                      }
                    />
                  </Flex>
                </Stack>
              </Stack>
            ))}
          </Flex>

          {/* Video section */}
          <Box css={styleVideoWrapper}>
            <Box>
              <AspectRatio ratio={16 / 9} css={styleVideoPlaceholder}>
                <Box css={styleVideoPlaceholderInner}>
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/1EFvXZjFqr0?si=QZi6AkEEdwp3C-Ii"
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </Box>
              </AspectRatio>
            </Box>
            <Stack spacing={8} css={styleVideoText}>
              <Text color="gray.9" css={styleVideoTitle}>
                {t('open.knowledge.testimonial.video.title')}
              </Text>
              <Text color="gray.7" css={styleVideoDesc}>
                <Trans
                  i18nKey="open.knowledge.testimonial.video.description"
                  components={{ br: <br /> }}
                />
              </Text>
            </Stack>
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
};

export default TestimonialsSection;

const ArticleButton = ({ link, onTrack }: { link: string; onTrack?: () => void }) => {
  const { t } = useTranslation();
  const handleClick = () => onTrack?.();
  return (
    <>
      <Button
        css={styleHideWhenMobile}
        component="a"
        target="_blank"
        href={link}
        variant="light"
        color="gray"
        size="sm"
        radius="xl"
        rightIcon={<Icon icon={faAngleRight} size="sm" color="gray.7" />}
        onClick={handleClick}
      >
        {t('open.knowledge.testimonial.read.more')}
      </Button>
      <ActionIcon
        css={styleHideWhenNotDesktop}
        component="a"
        target="_blank"
        href={link}
        variant="light"
        color="gray"
        radius="xl"
        size="sm"
        miw={36}
        h={36}
        aria-label={t('open.knowledge.testimonial.read.more')}
        onClick={handleClick}
      >
        <Icon icon={faAngleRight} size="sm" color="gray.7" />
      </ActionIcon>
    </>
  );
};

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff',
  padding: '100px 0',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '80px 0',
  },
});

const styleContainer = css({
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleContents = css({
  maxWidth: 1200,
  width: '100%',
});

const styleTitleStack = css({
  width: '100%',
  textAlign: 'center',
});

const styleSectionTitle = css({
  fontSize: 34,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 24,
  },
});

const styleSubTitle = css({
  fontSize: 22,
  fontWeight: 500,
  lineHeight: 1.35,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleCardRow = css({
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    flexDirection: 'column',
    gap: 20,
  },
});

const styleCard = css({
  flex: '1 0 0',
  borderRadius: 16,
  border: '1px solid #dee2e6',
  overflow: 'hidden',
  minWidth: 0,
  [desktopFirstMediaQuery.mobile]: {
    flex: 'none',
    width: '100%',
  },
});

const styleCardImage = css({
  width: '100%',
});

const styleCardContent = css({
  padding: 24,
  flexGrow: 1,
  [desktopFirstMediaQuery.mobile]: {
    padding: 16,
  },
});

const styleCardTitle = css({
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.35,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleCardDesc = css({
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.7,
  flexGrow: 1,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 14,
  },
});

const styleCardFooter = css({
  width: '100%',
});

const styleCardName = css({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.5,
});

const styleVideoWrapper = css({
  borderRadius: 16,
  border: '1px solid #dee2e6',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  boxShadow:
    '0px 1px 3px 0px rgba(0,0,0,0.05), 0px 28px 23px -7px rgba(0,0,0,0.05), 0px 12px 12px -7px rgba(0,0,0,0.04)',
  width: '100%',
});

const styleVideoPlaceholder = css({
  width: '100%',
  borderRadius: 12,
  overflow: 'hidden',
});

const styleVideoPlaceholderInner = css({
  width: '100%',
  height: '100%',
  backgroundColor: '#f1f3f5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const styleVideoText = css({
  padding: 32,
  [desktopFirstMediaQuery.mobile]: {
    padding: 16,
  },
});

const styleVideoTitle = css({
  fontSize: 26,
  fontWeight: 700,
  lineHeight: 1.35,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 20,
  },
});

const styleVideoDesc = css({
  fontSize: 20,
  fontWeight: 400,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 16,
  },
});

const styleHideWhenMobile = css({
  [desktopFirstMediaQuery.tablet]: {
    display: 'none',
  },
});

const styleHideWhenNotDesktop = css({
  [mobileFirstMediaQuery.desktop]: {
    display: 'none',
  },
});
